import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { usePutMeter } from "../../hooks";
import { useValidationContext } from "../../context";
import { logEvent } from "@shared/analytics/analytics";
import { usePremise } from "@features/cx/consumerManagement/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface Premise {
  code: string;
  name: string;
  // Add other premise properties as needed
}

interface AddMeterDialogProps {
  open: boolean;
  onClose: () => void;
  meterDetails: {
    deviceNumber: string;
    assignRoute: string;
    meterTypeDisplay: string;
  };
  meterId: any;
  remoteUtilityId: string | number;
}

export const AddMeterDialog: React.FC<AddMeterDialogProps> = ({
  open,
  onClose,
  meterDetails,
  meterId,
}) => {
  const { goToNextReading } = useValidationContext();
  const { mutate: putMeter } = usePutMeter();
  const remoteUtilityId =useRemoteUtilityId(); 
  // Fetch premises using hooks 
  const { data: premisesData, isLoading: isLoadingPremises } = usePremise({ 
    remote_utility_id: remoteUtilityId, 
    config_level: "premise", 
  });
  console.log("premisesData", premisesData);



  // Transform premises to options - memoized to prevent re-renders 
  const premiseOptions = useMemo(() => { 
    return ( 
      premisesData?.result?.map((premise: Premise) => ({ 
        value: premise.code, 
        label: premise.name, 
        data: premise, 
      })) || [] 
    ); 
  }, [premisesData]);

  const [formData, setFormData] = useState({
    deviceNumber: meterDetails.deviceNumber,
    premise: meterDetails.assignRoute || "",
    meterType: meterDetails.meterTypeDisplay,
  });

  const [errors, setErrors] = useState({
    premise: "",
  });

  const [searchTerm, setSearchTerm] = useState("");

  // Filter premises based on search term
  const filteredPremises = premiseOptions.filter((premise) =>
    premise.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const validateForm = () => {
    const newErrors = { premise: "" };
    
    if (!formData.premise) {
      newErrors.premise = "Assigned Premise is required";
    }

    setErrors(newErrors);
    return !newErrors.premise;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    putMeter(
      { 
        meterId, 
        payload: { 
          status: 0,
          premise: formData.premise 
        } 
      },
      {
        onSuccess: () => {
          logEvent("MX meter added to route", {
            meterId,
            premise: formData.premise,
          });
          goToNextReading();
          onClose();
        },
      }
    );
  };

  const handlePremiseChange = (value: string) => {
    setFormData(prev => ({ ...prev, premise: value }));
    if (errors.premise) {
      setErrors(prev => ({ ...prev, premise: "" }));
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      deviceNumber: meterDetails.deviceNumber,
      premise: meterDetails.assignRoute || "",
      meterType: meterDetails.meterTypeDisplay,
    });
    setErrors({ premise: "" });
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-md rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle>Add Meter to System</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Device Number - Read Only */}
          <div className="space-y-2">
            <Label htmlFor="deviceNumber" className="font-semibold">
              Device Number
            </Label>
            <Input
              id="deviceNumber"
              value={formData.deviceNumber}
              disabled
              className="bg-gray-50 cursor-not-allowed"
            />
          </div>

          {/* Assigned Premise - Dropdown with Search */}
          <div className="space-y-2">
            <Label htmlFor="premise" className="font-semibold">
              Assigned Premise <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.premise}
              onValueChange={handlePremiseChange}
            >
              <SelectTrigger 
                className={errors.premise ? "border-red-500" : ""}
              >
                <SelectValue placeholder="Select a premise" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder="Search premises..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-2"
                  />
                </div>
                {isLoadingPremises ? (
                  <div className="p-2 text-sm text-gray-500">
                    Loading premises...
                  </div>
                ) : filteredPremises.length > 0 ? (
                  filteredPremises.map((premise) => (
                    <SelectItem key={premise.value} value={premise.value}>
                      {premise.label}
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-sm text-gray-500">
                    {premiseOptions.length === 0 ? "No premises available" : "No premises found"}
                  </div>
                )}
              </SelectContent>
            </Select>
            {errors.premise && (
              <p className="text-sm text-red-500">{errors.premise}</p>
            )}
          </div>

          {/* Meter Type - Read Only */}
          <div className="space-y-2">
            <Label htmlFor="meterType" className="font-semibold">
              Meter Type
            </Label>
            <Input
              id="meterType"
              value={formData.meterType}
              disabled
              className="bg-gray-50 cursor-not-allowed"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">Confirm</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};